/**
 * useProfileStatistics Hook
 *
 * Custom hook for fetching user profile statistics
 * Uses React Query for data fetching with caching and automatic refetching
 */

import { useQuery } from "@tanstack/react-query"
import { getProfileStatistics } from "../services/profile-statistics.service"

export interface UseProfileStatisticsOptions {
  enabled?: boolean
  includeDrafts?: boolean
  includePaused?: boolean
}

/**
 * Hook to fetch user profile statistics
 * @param userId - The user ID to fetch statistics for
 * @param options - Optional configuration
 * @returns React Query result with statistics data
 */
export function useProfileStatistics(userId: string, options?: UseProfileStatisticsOptions) {
  const includeDrafts = options?.includeDrafts ?? false
  const includePaused = options?.includePaused ?? false
  return useQuery({
    queryKey: ["profileStatistics", userId, { includeDrafts, includePaused }],
    queryFn: () => getProfileStatistics(userId, includeDrafts, includePaused),
    enabled: !!userId && options?.enabled !== false, // Only run if userId exists
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache time (formerly cacheTime)
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: true, // Refetch when component mounts
  })
}
